import React, { useEffect, useMemo, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useCompetencyDrawerState } from 'components/Develop/competencyDrawerSlice';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { FeedbackSentiment } from 'interfaces/CF';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useProfilePage } from 'screens/Profile/hooks/dataHooks';
import { addMention } from 'screens/home/addPost/mentionHelper';
import useRouteQueryParams from 'screens/performanceReview/hooks/useRouteQueryParams';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';
import { useClearMutationCache } from 'utils/helpers/reactQueryHelpers';
import uuidv4 from 'utils/uuidv4';

import { modifyString, StringModifier } from 'constants/copyHelpers';
import { getQueryParams } from 'constants/helper';
import { sendOpenedSharedFeedbackEvent } from 'constants/mixpanelHelper';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';
import { routes } from 'constants/routes';

import { getApplicableCompetenciesForEmployees } from 'services/admin';

import Layout from '../common/Layout';
import MeshMavenTitleComp from '../common/MeshMaven/MeshMavenTitleComp';
import {
  setClearProvidedFeedbackData,
  setMeshMavenGroupId,
  setPrimaryPanelAction,
  setProvidedAnalysisError,
  setProvidedFeedbackPost,
  setProvidedFeedbackSentimentType,
  setProvidedGoal,
  setProvidedRephraseError,
  setProvidedSecondaryPanelAction,
  setProvidedSecondaryPanelOpen,
  setProvidedVisibility,
  useProvidedFeedbackData,
} from '../slice/feedbackSlice';
import { SecondaryPanelAction } from '../types/FeedbackSlice';
import { ShareFeedbackBody } from './ShareFeedbackBody';
import { useSaveShareFeedback } from './dataHooks';

interface Props {
  handleClose: () => void;
}

const ShareFeedback = ({ handleClose: handleCloseDrawer }: Props) => {
  const history = useHistory();
  const { moduleName } = useOrganisationSettings();
  const {
    feedbackPost,
    feedbackSentimentType,
    goal = {},
    isSecondaryPanelOpen,
    secondaryPanelAction,
    applicableEmployees,
  } = useProvidedFeedbackData();
  const [employeePrefilled, setEmployeePrefilled] = useState(false);

  const { employeeId: employeeIdRabbit } = useRouteQueryParams(routes.profile);
  const { employeeId: employeeIdDeer } = useRouteQueryParams(`/app/employee/:employeeId`);
  const { employeeId: employeeIdDeerDirectReport } = useRouteQueryParams(
    `/app/team/direct-reports/employee/:employeeId`
  );
  const { employeeId: employeeIdDeerAllReport } = useRouteQueryParams(`/app/team/all-reports/employee/:employeeId`);

  const employeeIdFromPath = checkNewNavEnabled()
    ? employeeIdDeer || employeeIdDeerDirectReport || employeeIdDeerAllReport
    : employeeIdRabbit;

  const {
    source,
    empId: employeeIdFromQuery,
    sentiment: sentimentFromQuery,
    competencyId,
    isSharePublicPraise: isSharePublicPraiseParam = false,
  } = getQueryParams(history.location.search);
  const [isSharePublicPraise, setSharePublicPraise] = useState(false);
  const employeeId = employeeIdFromPath ?? employeeIdFromQuery;

  const { data: providerData, isLoading } = useProfilePage({ employeeId });
  const competencyIdArrayKey = Array.isArray(competencyId) ? competencyId.join('') : competencyId;
  const { isOpen: isCompetencyDrawerOpen } = useCompetencyDrawerState();

  const competencyIdArray = useMemo(
    () => (Array.isArray(competencyId) ? competencyId : [competencyId]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [competencyIdArrayKey]
  );

  const dispatch = useDispatch();

  const clearMutationCache = useClearMutationCache();

  const queryClient = useQueryClient();

  useEffect(() => {
    const isSharePublicPraiseParamBool = isSharePublicPraiseParam === 'true';
    if (isSharePublicPraiseParamBool !== isSharePublicPraise) {
      // adding timeout because to close the panel we remove the current url , it makes the isSharePublicPraise false before panel got closed and render share feedback panel for 1 second which appears wrong
      setTimeout(() => setSharePublicPraise(isSharePublicPraiseParamBool), 500);
    }
  }, [isSharePublicPraiseParam]);

  const employeeToTag = (emp) => {
    const employee = { ...emp };
    employee.name = employee.fullName; // adding name key because draft js expects a name key to be there to use for displaying mention name
    employee.uuid = employee.id;
    const { metadata } = employee;
    return { employee, name: employee.name, metadata };
  };

  const onUpdateEditorState = (editorState) => {
    dispatch(
      setProvidedFeedbackPost({
        feedbackPost: editorState,
      })
    );
  };

  const fetchAndAddCompetencyTags = async (editorState) => {
    try {
      const res = await getApplicableCompetenciesForEmployees({
        competencyIds: competencyIdArray,
        employeeIds: [employeeId],
      });
      let newEditorState = editorState;
      res.forEach((competency) => {
        newEditorState = addMention(newEditorState, competency, ' #', '#', true);
      });
      setTimeout(() => {
        onUpdateEditorState(newEditorState);
        setEmployeePrefilled(true);
      }, 200);
    } catch (err) {
      console.log('Error fetching competency : ', err);
    }
  };

  const addEmployeeToEditorState = (editorState) => {
    // use it for adding a mention entity to editorState
    // Copied this addMention function from official mention plugin of draft js
    const newEditorState = addMention(editorState, employeeToTag(providerData), ' @', '@', true);
    if (!competencyIdArray.length || isCompetencyDrawerOpen) {
      setTimeout(() => {
        onUpdateEditorState(newEditorState);
        setEmployeePrefilled(true);
      }, 200);
      return;
    }
    fetchAndAddCompetencyTags(newEditorState);
  };

  const onFeedbackTypeSelect = (type: FeedbackSentiment) => {
    // set the appropriate visibility wrt to the feedback type
    dispatch(
      setProvidedVisibility({
        visibility: type === 'APPRECIATIVE' ? 'PUBLIC' : 'RECEIVER_AND_MANAGER',
      })
    );
    dispatch(
      setProvidedFeedbackSentimentType({
        feedbackSentimentType: type,
      })
    );
  };

  useEffect(() => {
    if (
      providerData?.id &&
      !isLoading &&
      feedbackPost.getCurrentContent().getPlainText('\n') === '' &&
      !employeePrefilled
    ) {
      addEmployeeToEditorState(feedbackPost);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerData?.id, isLoading, feedbackPost, employeePrefilled]);

  useEffect(() => {
    dispatch(setPrimaryPanelAction({ primaryPanelAction: 'SHARE' }));
    dispatch(setMeshMavenGroupId({ meshMavenGroupId: uuidv4() }));
  }, [dispatch]);

  useEffect(() => {
    if (sentimentFromQuery && sentimentFromQuery !== feedbackSentimentType) {
      onFeedbackTypeSelect(sentimentFromQuery as FeedbackSentiment);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sentimentFromQuery, feedbackSentimentType]);

  useEffect(() => {
    sendOpenedSharedFeedbackEvent({
      eventProperties: {
        source,
      },
    });
  }, [source]);

  const openSecondaryPanel = (action: SecondaryPanelAction) => {
    dispatch(
      setProvidedSecondaryPanelOpen({
        isSecondaryPanelOpen: true,
      })
    );
    dispatch(
      setProvidedSecondaryPanelAction({
        secondaryPanelAction: action,
      })
    );
  };

  const closeSecondaryPanel = () => {
    dispatch(
      setProvidedSecondaryPanelOpen({
        isSecondaryPanelOpen: false,
      })
    );
    dispatch(
      setProvidedRephraseError({
        error: null,
      })
    );
    dispatch(
      setProvidedAnalysisError({
        error: null,
      })
    );
    clearMutationCache([REACT_QUERY_KEYS.RephraseText]);
    clearMutationCache([REACT_QUERY_KEYS.FeedbackAnalysis]);
  };

  // eslint-disable-next-line no-shadow
  const onGoalSelect = (goal) => {
    dispatch(
      setProvidedGoal({
        goal,
      })
    );
  };

  const onClose = () => {
    queryClient.invalidateQueries([REACT_QUERY_KEYS.meshRecommends.nudgeCards]);
    clearMutationCache([REACT_QUERY_KEYS.RephraseText]);
    clearMutationCache([REACT_QUERY_KEYS.FeedbackAnalysis]);
    handleCloseDrawer();
    setTimeout(() => {
      dispatch(setClearProvidedFeedbackData());
    }, 500);
  };

  const { isSaving, onSave } = useSaveShareFeedback({ source: source as string, onClose });

  if (isLoading && employeeId) {
    return null;
  }

  return (
    <Layout
      header={isSharePublicPraise ? 'Share public praise' : 'Share Feedback'}
      onClose={onClose}
      onSave={onSave}
      showSecondaryPanel={isSecondaryPanelOpen}
      secondaryPanelAction={secondaryPanelAction}
      // @ts-expect-error: type error
      selectedId={goal?.uuid}
      onSelect={onGoalSelect}
      secondaryPanelHeader={
        secondaryPanelAction === 'GOAL_SELECTION' ? (
          `Related ${modifyString(moduleName, StringModifier.PLURAL_TITLE_CASE)}`
        ) : (
          <MeshMavenTitleComp />
        )
      }
      onSecondaryPanelClose={closeSecondaryPanel}
      applicableEmployees={applicableEmployees}
      saveButtonDisabled={applicableEmployees?.length === 0 || isSaving}
      saveButtonDisabledReason={
        isSaving ? 'Sharing your feedback' : 'You need to tag people to share feedback. Use @ to tag people'
      }
    >
      <ShareFeedbackBody
        source={source as string}
        openSecondaryPanel={openSecondaryPanel}
        isSharePublicPraise={isSharePublicPraise}
      />
    </Layout>
  );
};

export default ShareFeedback;
